import { App, Form, Input, Modal } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { auth, EmailAuthProvider, getFirebaseCode, reauthenticateWithCredential, updatePassword } from "@/lib/firebase";
import { firebaseAuthMessage } from "@/lib/firebase-auth-errors";

interface Values {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Change-password dialog for email/password users. Re-authenticates with the
 * current password (Firebase requires recent login for `updatePassword`) then
 * sets the new password.
 */
export function ChangePasswordModal({ open, onClose }: Props) {
  const { t } = useTranslation("auth");
  const { message } = App.useApp();
  const [form] = Form.useForm<Values>();
  const [loading, setLoading] = useState(false);

  async function onOk() {
    const values = await form.validateFields();
    const user = auth.currentUser;
    if (!user?.email) return;
    setLoading(true);
    try {
      const cred = EmailAuthProvider.credential(user.email, values.currentPassword);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, values.newPassword);
      message.success(t("changePassword.saved"));
      form.resetFields();
      onClose();
    } catch (err) {
      message.error(firebaseAuthMessage(getFirebaseCode(err)));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      title={t("changePassword.title")}
      open={open}
      onOk={onOk}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      okText={t("changePassword.save")}
      cancelText={t("changePassword.cancel")}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="currentPassword"
          label={t("changePassword.current")}
          rules={[{ required: true, message: t("changePassword.currentRequired") }]}
        >
          <Input.Password autoComplete="current-password" size="middle" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label={t("changePassword.new")}
          rules={[
            { required: true, message: t("changePassword.newRequired") },
            { min: 6, message: t("changePassword.tooShort") },
          ]}
        >
          <Input.Password autoComplete="new-password" size="middle" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label={t("changePassword.confirm")}
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: t("changePassword.confirmRequired") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                return Promise.reject(new Error(t("changePassword.mismatch")));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" size="middle" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
